import { Tabs } from 'antd';
import 'pure-react-carousel/dist/react-carousel.es.css';
import 'rc-slider/assets/index.css';
import { useEffect } from "react";
import 'react-datepicker/dist/react-datepicker.css';
import JSONInput from "react-json-editor-ajrm";
import locale from "react-json-editor-ajrm/locale/en";
import { useDispatch, useSelector } from 'react-redux';
import 'react-toastify/dist/ReactToastify.css';
import 'semantic-ui-css/semantic.min.css';
import ActionViewer from "./Components/ActionViewer/ActionViewer";
import ApiResponseModal from "./Components/ApiResponseModal/ApiResponseModal";
import BrandViewer from "./Components/BrandViewer/BrandViewer";
import CloseFile from "./Components/CloseFile/CloseFile";
import FileName from "./Components/FileName/FileName";
import FormDetailViewer from "./Components/FormDetailViewer/FormDetailViewer";
import LayoutViewer from "./Components/LayoutViewer/LayoutViewer";
import LoadingSpinnerModal from "./Components/LoadingSpinnerModal/LoadingSpinnerModal";
import PickAFile from "./Components/PickAFile/PickAFile";
import PreviewViewer from "./Components/PreviewViewer/PreviewViewer";
import QuestionViewer from "./Components/QuestionViewer/QuestionViewer";
import RefreshLoadedFile from "./Components/RefreshLoadedFile/RefreshLoadedFile";
import RuleViewer from "./Components/RuleViewer/RuleViewer";
import SaveAsCSVFile from "./Components/SaveAsCSVFile/SaveAsCSVFile";
import SaveAsFormClassFile from "./Components/SaveAsFormClassFile/SaveAsFormClassFile";
import SaveAsJSONFile from "./Components/SaveAsJSONFile/SaveAsJSONFile";
import SaveAsSQLFile from "./Components/SaveAsSQLFile/SaveAsSQLFile";
import SelectFile from "./Components/SelectFile/SelectFile";
import SelectTemplate from "./Components/SelectFile/SelectTemplate";
import ServiceQuestionBrandViewer from "./Components/ServiceQuestionBrandViewer/ServiceQuestionBrandViewer";
import ServiceQuestionViewer from "./Components/ServiceQuestionViewer/ServiceQuestionViewer";
import ServiceViewer from "./Components/ServiceViewer/ServiceViewer";
import UploadFile from "./Components/UploadFile/UploadFile";
import UploadTemplate from "./Components/UploadFile/UploadTemplate";
import { appSliceDispatch, standardQuestionSliceDispatch } from "./Constants/ReduxConstants";
import { FormContext } from "./DigitalForms/FormEngine";
import { ReduxHelpers } from "./Helpers/ReduxHelpers";
import './App.css';
import './App.less';
import './Theme/theme-variables.css';
import StandardQuestionViewer from './Components/StandardQuestions/StandardQuestionViewer';
import './DigitalForms/FormEngine';
import UploadFileService from "./Services/UploadFileService";
import './Theme/antdesignOverrides.css';
import './Theme/assistpoint.css';
import './Theme/fontawesome.css';
import './Theme/semanticOverrides.css';

// Import authentication components
import { AuthorizedRoute } from './auth/components/AuthorizedRoute';
import { IdleSessionManager } from './auth/components/IdleSessionManager';
import { useAuth } from './auth/AuthContext';
import { LogoutButton, UserInfo } from './auth/components/AuthComponents';
import { Layout, Space, Button } from 'antd';
import { LogoutOutlined } from '@ant-design/icons';

const { Header, Content } = Layout;

const DataModelViewerApp: React.FC = () => {
    const { isAuthenticated } = useAuth();
    const currentFileName = useSelector((state: any) => state.pickAFile.fileName);
    const currentDataContents = useSelector((state: any) => state.pickAFile.currentDataContents);
    const currentFormContext = useSelector((state: any) => state.pickAFile.formContext);
    const currentServiceList = useSelector((state: any) => state.pickAFile.serviceList);
    const currentBrandList = useSelector((state: any) => state.pickAFile.brandList);

    const activeTabIndex = useSelector((state: any) => state.app.activeTabIndex);

    const dispatch = useDispatch();

    const { CreateContentsFromStates } = ReduxHelpers();

    useEffect(() => {
        // The last element of the tabs section should always be the footer.
        let footerElement = document.getElementsByClassName('ant-tabs-tab');
        if (footerElement.length > 0) {
            (footerElement[footerElement.length - 1] as HTMLElement).style.pointerEvents = 'none';
        }
    });

    // Runs only on the first render
    useEffect(() => {
        // Force light theme only - disable dark mode completely
        document.documentElement.setAttribute('data-theme', 'light');
        document.documentElement.removeAttribute('data-theme-override');
    }, []);

    function onTabChange(key: string) {
        CreateContentsFromStates();

        dispatch({ type: appSliceDispatch.setActiveTabIndex, payload: key });
    }

    UploadFileService.getStandardFileContents()
        .then(response => {
            if (response.status === 200) {
                dispatch({ type: standardQuestionSliceDispatch.setQuestions, payload: response.data });
            }
            else {
                console.log('Bad response from GIT');
            }
        })
        .catch((e) => {
            console.log(e);
        });

    const preview = (
        <div>
            {!currentFileName && "Select a file to preview."}
            {currentFileName &&
                <FormContext.Provider value={currentFormContext}>
                    <PreviewViewer />
                </FormContext.Provider>
            }
        </div>
    );

    const formDetails = (
        <div>
            {!currentFileName && "Select a file to preview."}
            {currentFileName && <FormDetailViewer />}
        </div>
    );

    const brands = (
        <div>
            {!currentFileName && "Select a file to preview."}
            {currentFileName && <BrandViewer />}
        </div>
    );

    const services = (
        <div>
            {!currentFileName && "Select a file to preview."}
            {currentFileName &&
                <ServiceViewer />}
        </div>
    );

    const questions = (
        <div>
            {!currentFileName && "Select a file to preview."}
            {currentFileName &&
                <QuestionViewer />}
        </div>
    );

    const serviceQuestions = (
        <div>
            {!currentFileName && "Select a file to preview."}
            {(currentFileName && currentServiceList?.length === 0) && "Please define a service using the Services tab."}
            {(currentFileName && currentServiceList?.length > 0) &&
                <ServiceQuestionViewer />}
        </div>
    );

    const serviceQuestionsBrand = (
        <div>
            {!currentFileName && "Select a file to preview."}
            {(currentFileName && currentServiceList?.length === 0) && "Please define a service using the Services tab.  "}
            {(currentFileName && currentBrandList?.length === 0) && "Please define a brand using the Brands tab.  "}
            {(currentFileName && currentServiceList?.length > 0 && currentBrandList?.length > 0) &&
                <ServiceQuestionBrandViewer />}
        </div>
    );

    const layout = (
        <div>
            {!currentFileName && "Select a file to preview."}
            {currentFileName &&
                <LayoutViewer />}
        </div>
    );

    const rules = (
        <div>
            {!currentFileName && "Select a file to preview."}
            {currentFileName &&
                <div><RuleViewer />
                </div>}
        </div>
    );

    const actions = (
        <div>
            {!currentFileName && "Select a file to preview."}
            {currentFileName &&
                <div><ActionViewer />
                </div>}
        </div>
    );

    const dataContents = (
        <div>
            {!currentFileName && "Select a file to preview."}
            {currentFileName &&
                <JSONInput locale={locale} placeholder={currentDataContents ? JSON.parse(currentDataContents) : {}} width="100%" viewOnly={true} />}
        </div>
    );

    const standardQuestions = (
        <div>
            <StandardQuestionViewer />
        </div>
    );

    const panes = [
        {
            label: 'Previewer',
            children: preview,
            key: '1',
        },
        {
            label: 'Form Details',
            children: formDetails,
            key: '2',
        },
        {
            label: 'Brands',
            children: brands,
            key: '3',
        },
        {
            label: 'Services',
            children: services,
            key: '4',
        },
        {
            label: 'Questions',
            children: questions,
            key: '5',
        },
        {
            label: 'Service Questions',
            children: serviceQuestions,
            key: '6',
        },
        {
            label: 'Service Questions Brand',
            children: serviceQuestionsBrand,
            key: '7',
        },
        {
            label: 'Layout',
            children: layout,
            key: '8',
        },
        {
            label: 'Rules',
            children: rules,
            key: '9',
        },
        {
            label: 'Actions',
            children: actions,
            key: '10',
        },
        {
            label: 'Current Data Contents',
            children: dataContents,
            key: '11',
        },
        {
            label: 'Standard Questions',
            children: standardQuestions,
            key: '12'
        }
    ];

    const TabObject: React.FC = () => (
        <div>
            <Tabs tabPosition='left' onChange={onTabChange}
                items={panes} activeKey={activeTabIndex}
            />
        </div>
    );

    return (
        <div>
            <><div id="wrapper">
                <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    padding: '20px', 
                    background: 'var(--bg-primary)',
                    position: 'relative'
                }}>
                    {/* Theme toggle icon in top right - DISABLED */}
                    <div style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        display: 'none',
                        gap: '5px'
                    }}>
                        <button 
                            id="toggle-dark-mode" 
                            style={{
                                padding: '8px',
                                minWidth: 'auto',
                                borderRadius: '50%',
                                background: 'var(--button-bg)',
                                border: '1px solid var(--border-primary)',
                                color: 'var(--text-primary)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '16px',
                                width: '36px',
                                height: '36px'
                            }}
                            title="Toggle Dark Mode"
                        >
                            🌙
                        </button>
                        <button 
                            id="reset-to-system" 
                            style={{
                                padding: '8px',
                                minWidth: 'auto',
                                borderRadius: '50%',
                                background: 'var(--button-bg)',
                                border: '1px solid var(--border-primary)',
                                color: 'var(--text-primary)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                width: '36px',
                                height: '36px'
                            }}
                            title="Reset to System Theme"
                        >
                            ⚙️
                        </button>
                    </div>
                    
                    {/* Selected file header */}
                    {currentFileName && (
                        <div style={{ 
                            textAlign: 'center', 
                            marginBottom: '20px', 
                            fontSize: '18px',
                            fontWeight: 'bold',
                            color: 'var(--text-primary)'
                        }}>
                            Selected File: {currentFileName}
                        </div>
                    )}
                    
                    {/* Toolbar buttons */}
                    <div style={{ 
                        display: 'flex', 
                        gap: '8px', 
                        flexWrap: 'wrap', 
                        justifyContent: 'center',
                        marginBottom: '10px',
                        alignItems: 'center'
                    }}>
                        <SelectTemplate />
                        <PickAFile />
                        <SelectFile />

                        {currentFileName && (
                            <>
                                {/* Separator */}
                                <div style={{ 
                                    width: '2px', 
                                    height: '20px', 
                                    background: 'var(--border-primary)',
                                    margin: '0 8px'
                                }} />
                                
                                <RefreshLoadedFile />
                                <SaveAsJSONFile />
                                <SaveAsSQLFile />
                                <SaveAsFormClassFile />
                                <SaveAsCSVFile />
                                <UploadTemplate />
                                <UploadFile />
                                <CloseFile />
                            </>
                        )}
                    </div>
                    
                    {/* File name input - closer to toolbar */}
                    {currentFileName && (
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'center',
                            marginBottom: '20px'
                        }}>
                            <FileName />
                        </div>
                    )}
                </div>
                
                <div style={{ padding: '0 20px' }}>
                    <div>
                        <LoadingSpinnerModal />
                    </div>
                    <div>
                        <ApiResponseModal />
                    </div>
                    <TabObject />
                </div>
            </div></>
        </div>
    );
};

export default DataModelViewerApp;
